class InfoDrawer extends HTMLElement {
  static DISMISS_DISTANCE = 100;
  static DISMISS_VELOCITY = 0.45;
  static TRANSITION_MS = 250;

  constructor() {
    super();

    this._dialog = null;
    this._panel = null;
    this._header = null;
    this._drag = null;
    this._closeTimer = null;

    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onDialogCancel = this._onDialogCancel.bind(this);
    this._onDialogClick = this._onDialogClick.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
  }

  connectedCallback() {
    this.triggerPattern = this.dataset.triggerPattern || "#info";
    this._dialog = this.querySelector("dialog.info-drawer");
    this._panel = this.querySelector(".info-drawer__panel");
    this._header = this.querySelector(".info-drawer__header");

    if (!this._dialog) return;

    this._annotateTriggers();
    document.addEventListener("click", this._onDocumentClick);
    this._dialog.addEventListener("cancel", this._onDialogCancel);
    this._dialog.addEventListener("click", this._onDialogClick);

    if (this._header) {
      this._header.addEventListener("pointerdown", this._onPointerDown);
    }
  }

  disconnectedCallback() {
    document.removeEventListener("click", this._onDocumentClick);
    this._unbindDragListeners();
    clearTimeout(this._closeTimer);

    if (this._dialog) {
      this._dialog.removeEventListener("cancel", this._onDialogCancel);
      this._dialog.removeEventListener("click", this._onDialogClick);
    }

    if (this._header) {
      this._header.removeEventListener("pointerdown", this._onPointerDown);
    }
  }

  get open() {
    return Boolean(this._dialog?.open);
  }

  _annotateTriggers() {
    const showTriggerIcon = this.dataset.showTriggerIcon === "true";

    document.querySelectorAll("a[href]").forEach((link) => {
      if (link.closest("info-drawer")) return;

      const href = link.getAttribute("href");
      if (!href || !this._matchesPattern(href)) return;

      link.setAttribute("aria-haspopup", "dialog");
      if (showTriggerIcon) {
        link.classList.add("info-drawer-trigger");
      }
    });
  }

  _onDocumentClick(event) {
    // Don't treat links inside an open drawer as triggers.
    if (this.open && this.contains(event.target)) return;

    const link = event.target.closest("a[href]");
    if (!link || this.contains(link)) return;

    const href = link.getAttribute("href");
    if (!href || !this._matchesPattern(href)) return;

    event.preventDefault();
    this.show();
  }

  _matchesPattern(href) {
    const pattern = this.triggerPattern;
    if (!pattern) return false;

    const expectedHash = pattern.startsWith("#") ? pattern : `#${pattern}`;

    try {
      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin) return false;
      if (url.pathname !== window.location.pathname) return false;
      return url.hash === expectedHash;
    } catch {
      return href === pattern || href === expectedHash;
    }
  }

  _onDialogCancel(event) {
    // Keep the exit animation; native Escape would otherwise close immediately.
    event.preventDefault();
    this.hide();
  }

  _onDialogClick(event) {
    // Clicks on the dialog itself (outside the panel) dismiss, like a backdrop.
    if (event.target === this._dialog) {
      this.hide();
    }
  }

  _onPointerDown(event) {
    if (!this.open || !this._panel) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.target.closest("info-drawer-close, a, button, input, textarea, select")) return;

    this._drag = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startX: event.clientX,
      lastY: event.clientY,
      lastTime: performance.now(),
      velocityY: 0,
      active: false,
    };

    this._header.addEventListener("pointermove", this._onPointerMove);
    this._header.addEventListener("pointerup", this._onPointerUp);
    this._header.addEventListener("pointercancel", this._onPointerUp);
  }

  _onPointerMove(event) {
    if (!this._drag || event.pointerId !== this._drag.pointerId || !this._panel) return;

    const deltaY = event.clientY - this._drag.startY;
    const deltaX = event.clientX - this._drag.startX;
    const now = performance.now();
    const elapsed = Math.max(now - this._drag.lastTime, 1);

    this._drag.velocityY = (event.clientY - this._drag.lastY) / elapsed;
    this._drag.lastY = event.clientY;
    this._drag.lastTime = now;

    if (!this._drag.active) {
      // Require a clear downward swipe before capturing the gesture.
      if (deltaY < 8 || Math.abs(deltaY) < Math.abs(deltaX)) return;

      this._drag.active = true;
      this._dialog.classList.add("state--dragging");
      this._header.setPointerCapture(event.pointerId);
    }

    event.preventDefault();

    const offset = Math.max(0, deltaY);
    // Keep the horizontal centering transform while dragging vertically.
    this._panel.style.transform = `translate(-50%, ${offset}px)`;
    this._panel.style.opacity = String(Math.max(0.4, 1 - offset / 400));
  }

  _onPointerUp(event) {
    if (!this._drag || event.pointerId !== this._drag.pointerId || !this._panel) return;

    const deltaY = Math.max(0, event.clientY - this._drag.startY);
    const shouldDismiss =
      this._drag.active &&
      (deltaY >= InfoDrawer.DISMISS_DISTANCE || this._drag.velocityY >= InfoDrawer.DISMISS_VELOCITY);

    this._dialog.classList.remove("state--dragging");
    this._panel.style.transform = "";
    this._panel.style.opacity = "";
    this._unbindDragListeners();
    this._drag = null;

    if (shouldDismiss) {
      this.hide();
    }
  }

  _unbindDragListeners() {
    if (!this._header) return;

    this._header.removeEventListener("pointermove", this._onPointerMove);
    this._header.removeEventListener("pointerup", this._onPointerUp);
    this._header.removeEventListener("pointercancel", this._onPointerUp);
  }

  _resetDrag() {
    this._dialog?.classList.remove("state--dragging");
    this._unbindDragListeners();
    this._drag = null;

    if (this._panel) {
      this._panel.style.transform = "";
      this._panel.style.opacity = "";
    }
  }

  show() {
    if (!this._dialog) return;

    clearTimeout(this._closeTimer);

    if (!this._dialog.open) {
      this._dialog.showModal();
    }

    // Paint the off-screen bottom position first, then animate up.
    this._dialog.offsetHeight;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._dialog.classList.add("state--active");
      });
    });
  }

  hide() {
    if (!this._dialog?.open) return;

    this._resetDrag();
    this._dialog.classList.remove("state--active");

    clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => {
      if (!this._dialog.classList.contains("state--active") && this._dialog.open) {
        this._dialog.close();
      }
    }, InfoDrawer.TRANSITION_MS);
  }
}

customElements.define("info-drawer", InfoDrawer);

class InfoDrawerClose extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      this.closest("info-drawer")?.hide();
    });
  }
}

customElements.define("info-drawer-close", InfoDrawerClose);
